import { registerPlugin, WebPlugin } from '@capacitor/core';

/**
 * TypeScript contract for the Swift AuthPlugin bridge.
 *
 * At runtime Capacitor routes each call:
 *   - Inside the native iOS shell → Swift AuthPlugin
 *   - In a browser               → AuthWeb fallback below
 */
export interface AuthPlugin {
  startLogin(opts: {
    loginUrl: string;
    sessionUrlBase: string;
  }): Promise<{ authenticated: boolean; userUuid: string }>;
  isNative(): Promise<{ native: boolean }>;
  /** Dev helper — clears WKWebView cookies and storage for re-testing login. */
  clearSession(): Promise<{ cleared: boolean }>;
  /** Clears the saved region and re-presents the native region picker. */
  switchRegion(): Promise<void>;
}

/**
 * Web fallback — keeps browsers working without native code.
 * startLogin and clearSession are no-ops in the browser; isNative returns false.
 */
class AuthWeb extends WebPlugin implements AuthPlugin {
  async startLogin(): Promise<never> {
    throw new Error('Auth.startLogin() is native only — use the web login flow in a browser');
  }
  async isNative(): Promise<{ native: boolean }> {
    return { native: false };
  }
  async clearSession(): Promise<{ cleared: boolean }> {
    return { cleared: false };
  }
  async switchRegion(): Promise<void> {
    // no-op in browser
  }
}

export const Auth = registerPlugin<AuthPlugin>('Auth', {
  web: () => new AuthWeb(),
});
